import React from "react";
import { Link, useParams } from "react-router-dom";
import StoreButtons from "./ui/StoreButtons";
import { appDeepLink } from "../utils/deepLink";

/**
 * AppLinkLanding — web fallback for shared deep links (run.cards/meeting/:id,
 * /club/:id) opened WITHOUT the app installed.
 *
 * When the app IS installed, the OS opens it directly via the universal link /
 * App Link and this page never renders. When it isn't, the browser loads this
 * route, and we present a branded "open in app / download" surface (rather than
 * a 404). The "Open in app" button retries the custom scheme for the edge case
 * where the app is installed but the universal link didn't fire.
 *
 * Props:
 *  - type  'meeting' | 'club' | 'user'
 */
export default function AppLinkLanding({ type }) {
  const params = useParams();
  const id = params.id || params.meetingId || params.clubId || params.userId;
  const appUrl = appDeepLink(type, id);

  const label =
    type === "club" ? "this club" : type === "user" ? "this runner" : "this run";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-black px-5 py-10 text-white">
      {/* Ambient brand glow — matches the dark marketing sections. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(120,127,154,0.18),transparent_55%),radial-gradient(circle_at_80%_80%,rgba(58,106,76,0.14),transparent_55%)]"
      />

      <div className="relative w-full max-w-md text-center">
        {/* Brand mark */}
        <Link to="/" className="mb-8 inline-flex items-center gap-2.5" aria-label="RunCards home">
          <img src="/runcards_logo.png" alt="" aria-hidden className="h-8 w-8 object-contain" />
          <span className="font-runcards-light text-xl tracking-[0.01em] text-white">RunCards</span>
        </Link>

        <p className="mb-3 font-runcards-bold text-[11px] uppercase tracking-[0.32em] text-white/45">
          Open in the app
        </p>
        <h1 className="font-runcards-black text-[clamp(1.9rem,6vw,2.75rem)] leading-[1.05] tracking-[-0.02em]">
          See {label} in RunCards.
        </h1>
        <p className="mx-auto mt-4 max-w-sm font-runcards text-base text-white/65">
          The map, the pace, who's going, and one-tap join all live in the
          RunCards app.
        </p>

        {appUrl && (
          <a
            href={appUrl}
            className="mt-8 inline-flex w-full items-center justify-center rounded-xl bg-white px-5 py-3.5 font-runcards-bold text-sm text-black transition-colors hover:bg-white/90"
          >
            Open in app
          </a>
        )}

        <div className="mt-4">
          <p className="mb-3 font-runcards text-[11px] uppercase tracking-[0.22em] text-white/35">
            Don't have it yet?
          </p>
          <div className="flex justify-center">
            <StoreButtons variant="glass" className="!flex-row [&>a]:flex-1 sm:[&>a]:flex-none" />
          </div>
        </div>

        <Link
          to="/"
          className="mt-8 inline-block font-runcards text-xs uppercase tracking-[0.22em] text-white/40 transition-colors hover:text-white/70"
        >
          Back to RunCards
        </Link>
      </div>
    </div>
  );
}
